import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext();

const CART_STORAGE_KEY = 'sent2ment_cart_v1';

// Hydrate cart from localStorage on first render. Wrapped in try/catch so corrupted
// or unavailable storage (private mode, quota exceeded) never blocks the app.
const loadInitialItems = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Defensive normalisation: only keep items with a valid id and a positive quantity.
    return parsed
      .filter(i => i && typeof i.id === 'string' && typeof i.price === 'number')
      .map(i => ({ ...i, quantity: Math.max(1, Number(i.quantity) || 1) }));
  } catch (_err) {
    return [];
  }
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(loadInitialItems);
  const [isOpen, setIsOpen] = useState(false);

  // Persist cart on every change. Wrapped in try/catch so storage failures never break checkout.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch (_err) {
      // Quota exceeded / private mode — fail silently, in-memory cart still works.
    }
  }, [items]);

  const addItem = useCallback((product) => {
    setItems(prev => {
      const existing = prev.find(i => i.id === product.id);
      if (existing) {
        // Storage upgrade is single-quantity only
        if (product.id === 'storage-upgrade-25yr') return prev;
        return prev.map(i => i.id === product.id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    setIsOpen(true);
  }, []);

  const toggleStorageUpgrade = useCallback(() => {
    setItems(prev => {
      const has = prev.find(i => i.id === 'storage-upgrade-25yr');
      if (has) return prev.filter(i => i.id !== 'storage-upgrade-25yr');
      return [...prev, {
        id: 'storage-upgrade-25yr',
        name: 'Voice Storage Upgrade — 25 Years',
        collection: 'Add-On',
        price: 9.99,
        quantity: 1,
      }];
    });
  }, []);

  const hasStorageUpgrade = items.some(i => i.id === 'storage-upgrade-25yr');

  const removeItem = useCallback((id) => {
    setItems(prev => prev.filter(i => i.id !== id));
  }, []);

  const updateQuantity = useCallback((id, quantity) => {
    if (quantity < 1) {
      setItems(prev => prev.filter(i => i.id !== id));
      return;
    }
    setItems(prev => prev.map(i => i.id === id ? { ...i, quantity } : i));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return (
    <CartContext.Provider value={{
      items, isOpen, setIsOpen,
      addItem, removeItem, updateQuantity, clearCart,
      toggleStorageUpgrade, hasStorageUpgrade,
      totalItems, totalPrice,
    }}>
      {children}
    </CartContext.Provider>
  );
};
